import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShieldCheck, Warning, MagnifyingGlass, CurrencyCircleDollar, Lightning } from "@phosphor-icons/react";

// Code-based animated hero promo (~25s loop) that replaces the old MP4 + GIF poster.
// Same storyline as the original promo, with reconciled 2026 copy:
//  refusal notice -> workspace 10s pre-audit -> discrepancy report -> 1-click sample -> guarantee -> brand.
// No accuracy %, no storage claim, no ICC statistic. Standards always read "UCP 600 / ISBP 821 (2023)".
const DURATIONS = [4200, 4400, 4800, 3800, 5000, 3200]; // ms per scene (~25.4s total)

const Scene = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    transition={{ duration: 0.5 }}
    className="absolute inset-0 flex flex-col p-5 sm:p-6"
  >
    {children}
  </motion.div>
);

const Reveal = ({ delay = 0, children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const scenes = [
  // 0 — Bank advice of refusal
  (
    <Scene>
      <div className="flex h-full flex-col justify-center gap-3" data-testid="promo-scene-refusal">
        <Reveal className="flex items-center gap-2 text-lc-critical">
          <Warning weight="fill" size={16} />
          <span className="font-mono text-[11px] tracking-widest">SWIFT MT734 · ISSUING BANK</span>
        </Reveal>
        <Reveal delay={0.08}><h3 className="font-serif text-xl sm:text-2xl text-white">ADVICE OF REFUSAL</h3></Reveal>
        <ul className="space-y-1.5">
          {["LATE SHIPMENT", "AMOUNT EXCEEDS TOLERANCE", "B/L LACKS ON BOARD DATE"].map((d, k) => (
            <Reveal key={d} delay={0.2 + k * 0.12}>
              <li className="flex items-center gap-2 font-mono text-[12px] text-lc-fog">
                <span className="text-lc-critical">✕</span>{d}
              </li>
            </Reveal>
          ))}
        </ul>
        <Reveal delay={0.7}>
          <div className="inline-flex w-fit items-center gap-2 rounded-sm border border-lc-critical/40 bg-lc-critical/10 px-3 py-1.5 font-mono text-[12px] text-lc-critical">
            DISCREPANCY FEE: USD 50–150
          </div>
        </Reveal>
      </div>
    </Scene>
  ),
  // 1 — LC Shield workspace, 10-second pre-audit of a SWIFT MT700 draft
  (
    <Scene>
      <div className="flex h-full flex-col justify-center gap-3" data-testid="promo-scene-workspace">
        <Reveal><span className="font-mono text-[11px] tracking-widest text-lc-sky">LC SHIELD WORKSPACE</span></Reveal>
        <Reveal delay={0.08}><h3 className="font-serif text-xl sm:text-2xl text-white">Typically under 30s pre-audit</h3></Reveal>
        <Reveal delay={0.18}>
          <div className="rounded-md border border-white/10 bg-lc-slate/60 p-3 font-mono text-[11px] leading-relaxed text-lc-steel">
            <div className="text-lc-fog">SWIFT MT700 DRAFT</div>
            <div className="opacity-70">:31D: DATE/PLACE OF EXPIRY …</div>
            <div className="opacity-70">:44C: LATEST SHIPMENT DATE …</div>
            <div className="opacity-70">:45A: DESCRIPTION OF GOODS …</div>
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="flex items-center gap-2 text-[12px] text-lc-sky">
            <MagnifyingGlass size={14} /> Scanning draft… <span className="font-mono">00:28</span>
          </div>
        </Reveal>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
          <motion.div className="h-full bg-lc-sky" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 3.6, ease: "linear" }} />
        </div>
      </div>
    </Scene>
  ),
  // 2 — Discrepancy report with article references
  (
    <Scene>
      <div className="flex h-full flex-col justify-center gap-2.5" data-testid="promo-scene-report">
        <Reveal><span className="font-mono text-[11px] tracking-widest text-lc-sky">DISCREPANCY REPORT</span></Reveal>
        <div className="space-y-1.5">
          {[
            ["Late shipment", "UCP 600 Art. 20 · ISBP 821 (2023)"],
            ["Amount exceeds tolerance", "UCP 600 Art. 18(b) · field 39A"],
            ["B/L lacks on-board date", "UCP 600 Art. 20 · ISBP 821 (2023)"],
          ].map(([t, ref], k) => (
            <Reveal key={t} delay={0.12 + k * 0.14}>
              <div className="flex items-start gap-2 rounded-sm border border-lc-warn/30 bg-lc-warn/10 px-2.5 py-1.5">
                <Warning size={13} weight="fill" className="mt-0.5 shrink-0 text-lc-warn" />
                <div>
                  <div className="text-[12px] text-white">{t}</div>
                  <div className="font-mono text-[10px] text-lc-steel">{ref}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.6}>
          <p className="font-mono text-[10px] text-lc-steel">Rule-based checks against UCP 600 and ISBP 821 (2023)</p>
        </Reveal>
      </div>
    </Scene>
  ),
  // 3 — 1-Click Sample Audit at $15 / HK$99
  (
    <Scene>
      <div className="flex h-full flex-col items-start justify-center gap-3" data-testid="promo-scene-sample">
        <Reveal className="inline-flex items-center gap-2 text-lc-sky">
          <Lightning weight="fill" size={16} />
          <span className="font-mono text-[11px] tracking-widest">1-CLICK SAMPLE AUDIT</span>
        </Reveal>
        <Reveal delay={0.1}><h3 className="font-serif text-2xl sm:text-3xl text-white">Try it in one click</h3></Reveal>
        <Reveal delay={0.24}>
          <div className="inline-flex items-center gap-2 rounded-md border border-lc-sky/40 bg-lc-sky/10 px-4 py-2 text-white">
            <CurrencyCircleDollar size={18} className="text-lc-sky" />
            <span className="text-sm font-semibold">$15 / HK$99 · Single Audit Pass</span>
          </div>
        </Reveal>
      </div>
    </Scene>
  ),
  // 4 — Audit Fee Guarantee (reconciled wording)
  (
    <Scene>
      <div className="flex h-full flex-col justify-center gap-3" data-testid="promo-scene-guarantee">
        <Reveal className="inline-flex items-center gap-2 text-lc-ok">
          <ShieldCheck weight="fill" size={18} />
          <span className="font-mono text-[11px] tracking-widest">AUDIT FEE GUARANTEE</span>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="text-[13px] sm:text-sm leading-relaxed text-white">
            If a discrepancy we missed appears in your bank's refusal notice, we refund the audit fee.
            Send the notice within <span className="text-lc-ok">30 days</span>.{" "}
            <span className="text-lc-steel">Terms apply.</span>
          </p>
        </Reveal>
      </div>
    </Scene>
  ),
  // 5 — Brand / CTA
  (
    <Scene>
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center" data-testid="promo-scene-brand">
        <Reveal className="flex items-center gap-2">
          <ShieldCheck weight="fill" size={22} className="text-lc-sky" />
          <span className="font-serif text-2xl text-white">LC Shield</span>
        </Reveal>
        <Reveal delay={0.12}><p className="text-[13px] text-lc-fog">Pre-audit your LC before the bank does</p></Reveal>
        <Reveal delay={0.24}><span className="font-mono text-[12px] text-lc-sky">getlcshield.com</span></Reveal>
      </div>
    </Scene>
  ),
];

export const HeroPromoVideo = () => {
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setI((p) => (p + 1) % scenes.length), DURATIONS[i]);
    return () => clearTimeout(t);
  }, [i]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-xl"
      data-testid="hero-promo-video"
    >
      <div
        className="relative aspect-video overflow-hidden rounded-lg border-2 bg-lc-navy shadow-[0_0_40px_-8px_rgba(56,189,248,0.45)]"
        style={{ borderColor: "#1E293B" }}
        role="img"
        aria-label="LC Shield animated demo: a bank advice of refusal, a LC Shield pre-audit of a SWIFT MT700 draft (typically under 30 seconds), a discrepancy report citing UCP 600 / ISBP 821 (2023), a $15 / HK$99 single audit pass, and the Audit Fee Guarantee."
      >
        {/* soft radial depth + grid */}
        <div className="pointer-events-none absolute inset-0 opacity-60"
          style={{ background: "radial-gradient(120% 80% at 80% 0%, rgba(56,189,248,0.12), transparent 60%)" }} />
        <div className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "linear-gradient(#38bdf8 1px, transparent 1px), linear-gradient(90deg, #38bdf8 1px, transparent 1px)", backgroundSize: "28px 28px" }} />

        <AnimatePresence mode="wait">
          <div key={i} className="absolute inset-0">{scenes[i]}</div>
        </AnimatePresence>

        {/* scene progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
          <motion.div key={i} className="h-full bg-lc-sky"
            initial={{ width: "0%" }} animate={{ width: "100%" }}
            transition={{ duration: DURATIONS[i] / 1000, ease: "linear" }} />
        </div>

        {/* corner label */}
        <div className="absolute right-3 top-3 rounded-full border border-white/15 bg-lc-navy/70 px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest text-lc-sky backdrop-blur-md">
          Auto-play demo
        </div>

        {/* cyan inner ring */}
        <div className="pointer-events-none absolute inset-0 rounded-lg ring-1 ring-inset ring-lc-sky/20" />
      </div>
    </motion.div>
  );
};

export default HeroPromoVideo;
